import React, { useEffect, useState } from "react";
import { Wifi, WifiOff, CloudUpload, CheckCircle2, AlertTriangle, Smartphone } from "lucide-react";
import { listAttendance, listPending } from "../lib/db";
import { flushQueue, bindAutoSync } from "../lib/pwaSync";
import { requestSyncNow } from "../serviceWorkerRegistration";

export default function PwaStatusBar({ onQueueChange }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState(0);
  const [flushing, setFlushing] = useState(false);
  const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

  const refresh = async () => {
    const all = await listAttendance();
    setPending(all.filter((r) => r.sync_status === "PENDING").length);
    setFailed(all.filter((r) => r.sync_status === "FAILED").length);
    onQueueChange && onQueueChange(all);
  };

  useEffect(() => {
    refresh();
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    bindAutoSync(refresh);
    const i = setInterval(refresh, 5000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); clearInterval(i); };
  }, []);

  const flush = async () => {
    setFlushing(true);
    requestSyncNow();
    await flushQueue();
    await refresh();
    setFlushing(false);
  };

  let statusText = "Ready to capture attendance";
  let statusTone = "ok";
  if (!online && pending === 0 && failed === 0) { statusText = "Offline · ready to capture, will sync later"; statusTone = "warn"; }
  if (pending > 0) { statusText = `Captured safely · ${pending} awaiting server verification`; statusTone = "pending"; }
  if (failed > 0) { statusText = `${failed} capture${failed > 1 ? "s" : ""} failed sync · retry manually`; statusTone = "fail"; }
  const showPendingCopy = pending > 0 || failed > 0 || !online;

  const toneClass =
    statusTone === "ok" ? "border-[#22C55E] bg-[#DCFCE7] text-[#166534]"
    : statusTone === "pending" ? "border-[#EAB308] bg-[#FEF9C3] text-[#854D0E]"
    : statusTone === "fail" ? "border-[#EF4444] bg-[#FEE2E2] text-[#991B1B]"
    : "border-neutral-400 bg-neutral-100 text-neutral-800";

  return (
    <div data-testid="pwa-status" className="space-y-1">
      <div className={`border ${toneClass} px-3 py-2 flex items-center gap-2`}>
        <span className="inline-flex items-center gap-1 mono-label" style={{ fontSize: 10 }}>
          {online ? <Wifi size={12} /> : <WifiOff size={12} />}
          {online ? "ONLINE" : "OFFLINE"}
        </span>
        <span className="mono-label opacity-60">·</span>
        <span className="text-xs font-semibold flex-1 truncate">{statusText}</span>
        {standalone && (
          <span className="mono-label inline-flex items-center gap-1" style={{ fontSize: 10 }} data-testid="pwa-standalone">
            <Smartphone size={11} /> APP
          </span>
        )}
        {(pending > 0 || failed > 0) && (
          <button
            data-testid="btn-flush-queue"
            onClick={flush}
            disabled={flushing || !online}
            className="h-7 px-2 border border-current font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-50"
          >
            <CloudUpload size={12} /> {flushing ? "Syncing…" : "Sync now"}
          </button>
        )}
      </div>
      {showPendingCopy && (
        <div data-testid="pwa-pending-note" className="mono-label text-neutral-300" style={{ fontSize: 9 }}>
          PENDING SYNC · WILL UPLOAD TO PEOPLE ENGINE WHEN ONLINE.
        </div>
      )}
    </div>
  );
}
